import React, { forwardRef } from 'react';
import type { AnchorHTMLAttributes } from 'react';
import { resolve, useBaseContext, useLocationContext } from '@gatsbyjs/reach-router';

// gatsby-node aliases `react-router-dom` to this module. Shared components such as rainbow
// `Text.Link` render a router `Link` for internal URLs, which would otherwise be a client side
// transition. Here it is a plain anchor so the browser does a full page load (see document-navigation).
export * from '@gatsbyjs/reach-router';

type To = string | { pathname?: string; search?: string; hash?: string };

interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
    to: To;
    replace?: boolean;
    state?: unknown;
}

const prefixed = (prefix: string, part = ''): string => (!part || part.startsWith(prefix) ? part : `${prefix}${part}`);

const toPath = (to: To): string =>
    typeof to === 'string' ? to : `${to.pathname ?? ''}${prefixed('?', to.search)}${prefixed('#', to.hash)}`;

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
    ({ to, replace: _replace, state: _state, ...rest }, ref) => {
        const { baseuri } = useBaseContext();
        const location = useLocationContext()?.location;
        // Same href and aria-current as reach-router's Link, so relative paths resolve against the route
        const href = resolve(toPath(to), baseuri);

        return (
            <a
                ref={ref}
                aria-current={location?.pathname === encodeURI(href) ? 'page' : undefined}
                {...rest}
                href={href}
            />
        );
    },
);

Link.displayName = 'Link';
